import React, { useEffect, useMemo, useState } from "react";

import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import _ from 'lodash';
import cx from 'classnames';

import styles from './uncategorized.module.scss';
import Actions from "src/components/actionbar";
import InfiniteScroll from "src/components/infiniteScroll";
import { deleteUncategorizedAssets, fetchAssetsFromUncategorisedCollection, invalidateUncategorizedAssetsDeletion, sortUncategorizedAssets } from "src/redux/actions/collections";
import { useSelector } from "react-redux";
import { Asset } from "src/types/assets";
import LinesEllipsis from "react-lines-ellipsis";
import { FileService } from "src/services/file.service";
import { useTransition, animated, a } from "@react-spring/web";
import Reveal from "src/components/animation";
import Button from "src/components/button";
import Toolbar from "src/components/toolbar";
import Thumbnail from "src/components/thumbnail";
import Slider from "src/components/slider";
import { AssetCard } from "src/components/asset/add/card";
import pluralize from "pluralize";
import dateFormat from 'dateformat';
import { NavigateEntity } from "src/components/NavigateEntity";

const UncategorizedCollectionPage: React.FC<object> = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [edit, setEdit] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState<Asset[]>([]);
  const [sortBy, setSortBy] = useState<{ type: string, order: 'asc' | 'desc' }>({
    type: "name",
    order: "asc"
  });

  const { assets, loading, hasMoreItems, nextPage, total } = useSelector((state) => {
    const collectionState = _.get(state, 'collections.uncategorized');

    const assets = _.get(collectionState, 'items') || [];
    const loading = _.get(collectionState, 'loading');
    const pagination = _.get(collectionState, 'pagination');
    const total = _.get(pagination, "totalPages", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems");

    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage && nextPage >= total) {
      nextPage = null;
    }

    return {
      assets,
      loading,
      hasMoreItems,
      nextPage,
      total
    };
  }, _.isEqual);

  useEffect(() => {
    // Fetch collection data
    fetchCollectionForPage(0);
  }, []);

  useEffect(() => {
    dispatch(sortUncategorizedAssets(sortBy));
  }, [sortBy]);

  const fetchCollectionForPage = (page: number) => {
    dispatch(fetchAssetsFromUncategorisedCollection({
      page: page,
      limit: 20
    }));
  }

  const handleLoadMoreAssets = () => {
    if (nextPage) {
      fetchCollectionForPage(nextPage);
    }
  }

  const handleBack = () => {
    navigate(-1);
  };

  const handleAssetClick = (asset: Asset) => {
    const item = _.find(selectedAssets, { id: asset.id });
    let items = [...selectedAssets];
    if (item) {
      items = selectedAssets.filter((item) => item.id !== asset.id);
    } else {
      items = [...selectedAssets, asset];
      items = _.uniqBy(items, 'id');
    }

    setSelectedAssets(items);
  }

  const handleHeaderSort = (type: string) => {
    setSortBy({
      type: type,
      order: sortBy.order === 'asc' ? 'desc' : 'asc'
    });
  }

  return (
    <div className={styles.container}>
      <div className={styles.menubar}>
        <div className={styles.left}>
          <div className={styles.header}>
            <button className={styles.backButton} onClick={handleBack}>
              <i className="paperflite-arrow-up"></i>
            </button>
            <div className={styles.title}>Uncategorized Collection</div>
          </div>
          <div className={styles.message}>
            Since we cannot hope for order, let us withdraw with style from the chaos.
            This is an automatically curated collection of all the content that are not a part of your collections or streams.
          </div>
        </div>

        <Actionbar edit={edit} assets={selectedAssets} onChange={(value) => {
          setEdit(value);
          if (!value) {
            setSelectedAssets([]);
          }
        }} />
      </div>

      <div className={styles.assets}>
        <div className={cx(styles.row, styles.header)}>
          <div className={cx(styles.asset, styles.sortable)} onClick={() => handleHeaderSort('name')}>
            Name<SortAction type="name" value={sortBy} />
          </div>
          <div className={styles.createdBy}>Created By</div>
          <div className={cx(styles.views, styles.sortable)} onClick={() => handleHeaderSort('views')}>
            Views <SortAction type="views" value={sortBy} />
          </div>
          <div className={cx(styles.shares, styles.sortable)} onClick={() => handleHeaderSort('shares')}>
            Shares <SortAction type="shares" value={sortBy} />
          </div>
          <div className={cx(styles.downloads, styles.sortable)} onClick={() => handleHeaderSort('downloads')}>
            Downloads<SortAction type="downloads" value={sortBy} />
          </div>
          <div className={cx(styles.timespent, styles.sortable)} onClick={() => handleHeaderSort('timespent')}>
            Timespent <SortAction type="timespent" value={sortBy} />
          </div>
        </div>
        <InfiniteScroll
          hasMore={hasMoreItems}
          loading={loading}
          loadMore={handleLoadMoreAssets}
          loader={() => {
            const count = _.size(assets) === 0 ? 4 : 1;
            return _.times(count, (index) => (
              <AssetRowPlaceholder key={index} />
            ));
          }}
          className={styles.grid}>
          {assets.map((item: any) => {
            const selected = _.find(selectedAssets, { id: item.id }) != null;

            return (
              <AssetRow key={item.id}
                item={item}
                selection={edit}
                selected={selected}
                onSelect={handleAssetClick}
              />
            )
          })}
        </InfiniteScroll>
      </div>

      <DeleteSelectionToolbar show={edit} assets={selectedAssets} onFinish={() => { }} />
    </div>
  );
}

interface SortActionProps {
  type: string;
  value: {
    type: string;
    order: string;
  }
}

const SortAction: React.FC<SortActionProps> = ({ type, value }) => {
  return (
    <button className={cx(styles.sortAction, {
      [styles.active]: value.type === type
    })}>
      <i className={cx("paperflite-dropdown", styles.up, {
        [styles.selected]: value.order === 'asc'
      })} />
      <i className={cx("paperflite-dropdown", styles.down, {
        [styles.selected]: value.order === 'desc'
      })} />
    </button>
  );
}

interface ActionbarProps {
  edit: boolean;
  assets: Asset[];
  // eslint-disable-next-line no-unused-vars
  onChange: (value: boolean) => void;
}

const Actionbar: React.FC<ActionbarProps> = ({ edit, assets, onChange }) => {
  const [transitions, api] = useTransition(!edit, () => ({
    initial: { opacity: 0, top: "0px" },
    from: { opacity: 1, top: "-90px" },
    enter: { opacity: 1, top: "0px" },
    leave: { opacity: 0, top: "-90px" },
  }));

  useEffect(() => {
    api.start();
  }, [edit]);

  return (
    <div className={styles.actionBar}>
      {transitions(
        (style, item) =>
          item && (
            <animated.div style={style} className={cx(styles.deleteBar)}>
              <Actions.Row>
                <Actions.Button icon="paperflite-delete" width={3} title="Delete Assets" onClick={() => onChange(true)} />
              </Actions.Row>
            </animated.div>
          )
      )}
      <DeleteMenubar show={edit} assets={assets} onCancel={() => onChange(false)} />
    </div>
  );
}

interface DeleteMenubarProps {
  show: boolean;
  assets: Asset[];
  onCancel: () => void;
}

export const DeleteMenubar: React.FC<DeleteMenubarProps> = ({
  show,
  assets,
  onCancel,
}) => {
  const deleting = false;
  const [confirm, setConfirm] = React.useState(false);

  const onConfirm = () => {
    if (_.size(assets) == 0) {
      return;
    }

    setConfirm(true);
  };

  const handleConfirm = () => {
    setConfirm(false);
    onCancel();
  };

  return (
    <Reveal show={show} className={styles.deleteConfirmation}>
      <div className={styles.message}>
        Click on one or more assets you wish to delete.
      </div>
      <Button
        variant="primary"
        icon="paperflite-delete"
        text={deleting ? "Deleting..." : "Delete"}
        disabled={_.size(assets) == 0}
        onClick={onConfirm}
      />
      <Button variant="secondary" text="Cancel" onClick={onCancel}></Button>
      <DeleteConfirmationDialog
        show={confirm}
        assets={assets}
        onFinish={handleConfirm}
        onClose={() => setConfirm(false)}
      />
    </Reveal>
  );
};

interface DeleteSelectionToolbarProps {
  show: boolean;
  assets: any[];
  onFinish: () => void;
}

export const DeleteSelectionToolbar: React.FC<DeleteSelectionToolbarProps> = ({
  show,
  assets,
  onFinish,
}) => {
  const [confirm, setConfirm] = React.useState(false);

  const handleShowConfirm = () => {
    setConfirm(true);
  };

  const handleConfirm = () => {
    setConfirm(false);
    onFinish();
  };

  return (
    <>
      <Toolbar.Container show={show}>
        <Toolbar.Text>
          <span>{_.size(assets)} Selected</span>
        </Toolbar.Text>
        <Toolbar.Divider />
        <Toolbar.IconButton
          icon="paperflite-delete"
          disabled={_.size(assets) == 0}
          onClick={handleShowConfirm}
        />
      </Toolbar.Container>
      <DeleteConfirmationDialog
        show={confirm}
        assets={assets}
        onFinish={handleConfirm}
        onClose={() => setConfirm(false)}
      />
    </>
  );
};

interface DeleteConfirmationDialogProps {
  show: boolean;
  assets: Asset[];
  onFinish: () => void;
  onClose: () => void;
}

export const DeleteConfirmationDialog: React.FC<DeleteConfirmationDialogProps> = ({ show, assets, onFinish, onClose }) => {
  const dispatch = useDispatch();

  const deleting = useSelector((state: any) => {
    return _.get(state, 'collections.uncategorized.deletion.deleting');
  });

  const deleted = useSelector((state: any) => {
    return _.get(state, 'collections.uncategorized.deletion.deleted');
  });

  const handleConfirm = () => {
    if (_.size(assets) == 0) {
      return;
    }

    const request = assets.map((asset) => {
      return asset.id;
    });

    dispatch(deleteUncategorizedAssets({
      assets: request,
    }));
  };

  const handleCancel = () => {
    if (onClose) {
      onClose();
    }
  };

  useEffect(() => {
    if (!deleting && deleted) {
      dispatch(invalidateUncategorizedAssetsDeletion());
      onFinish();
    }
  }, [deleting, deleted]);

  return (
    <Slider.Dialog
      className={styles.deleteConfirmDialog}
      width="300"
      show={show}
      onClose={onClose}
    >
      <Slider.Header
        title="deleting..."
        message={`${_.size(assets)} ${pluralize("asset", _.size(assets))}`}
      >
        <p className={styles.description} style={{ marginBottom: 10 }}>
          Are You Sure? Are you really ready to say goodbye to these
          assets? No takebacksies!
        </p>
      </Slider.Header>
      <Slider.Body>
        <div className={styles.grid}>
          {assets.map((asset) => {
            return <AssetCard key={asset.id} asset={asset} editable={false} />;
          })}
        </div>
      </Slider.Body>
      <Slider.Actions>
        <Button
          icon="paperflite-tick"
          text={deleting ? "Deleting..." : "Confirm"}
          variant="primary"
          disabled={deleting}
          onClick={handleConfirm}
        />
        <Button
          text="Cancel"
          variant="secondary"
          disabled={deleting}
          onClick={handleCancel}
        />
      </Slider.Actions>
    </Slider.Dialog>
  );
};

// Assets
interface AssetRowProps {
  item: any;
  selection: boolean;
  selected: boolean;

  // eslint-disable-next-line no-unused-vars
  onSelect: (asset: Asset) => void;
}

const AssetRow: React.FC<AssetRowProps> = ({ item, selection, selected, onSelect }) => {
  const navigate = useNavigate();
  const asset = item.asset as Asset;
  const analtyics = item.analytics;

  const duration = useMemo(() => {
    const value = FileService.formatDuration(_.get(analtyics, 'item.totalTimeSpent', 0), 'short');
    return `~${value}`;
  }, [analtyics]);

  const fileSize = useMemo(() => {
    const size = _.get(asset, 'metadata.content_length', 0);
    if (size <= 0) {
      return false;
    }

    return FileService.formatSize(size);
  }, [asset]);

  const author = useMemo(() => {
    const user = _.get(asset, 'author');
    return `${_.get(user, 'firstName', '')} ${_.get(user, 'lastName', '')}`;
  }, [asset]);

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (selection) {
      onSelect(asset);
    } else {
      navigate(`/assets/${asset.id}?source=collections/uncategorized`);
    }
  }

  return (
    <div className={cx(styles.row, {
      [styles.selection]: selection,
      [styles.selected]: selected,
    })}>
      <NavigateEntity
      path={`/assets/${asset.id}?source=collections/uncategorized`}
      onClick={handleClick} 
      >
      <div className={styles.asset}>
        <div className={styles.icon}>
          <Thumbnail
            media={asset.icon}
            width={100}
            className={cx(styles.thumbnail)}
            alt=""
            defaultImage="/images/assets/default_asset.jpg"
          />
          <div className={styles.overlay}></div>
          <div className={styles.radio}>
            <i className={cx({
              "paperflite-radio-button-unselected": !selected,
              "fa-solid fa-check-circle": selected
            })}></i>
          </div>
        </div>

        <div className={styles.details}>
          <div className={styles.name}>
            <LinesEllipsis text={asset.name || "Untitled"} maxLine='2' ellipsis='...' trimRight basedOn='letters' />
          </div>
          <div className={styles.metadata}>
            {fileSize && <span className={styles.size}>{fileSize}</span>}
            <span className={styles.fileType}>{FileService.getFileType(asset?.metadata)}</span>
          </div>
          <div className={styles.updatedDate}>Last Updated {dateFormat(asset?.contentModifiedDate, 'dd mmm yyyy')}</div>
        </div>
      </div>
      </NavigateEntity>
      <div className={styles.createdBy}>{author}</div>
      <div className={styles.views}>
        {analtyics?.loading ? <AnalyticsPlaceholder /> : _.get(analtyics, 'item.views', 0)}
      </div>
      <div className={styles.shares}>
        {analtyics?.loading ? <AnalyticsPlaceholder /> : _.get(analtyics, 'item.shares', 0)}
      </div>
      <div className={styles.downloads}>
        {analtyics?.loading ? <AnalyticsPlaceholder /> : _.get(analtyics, 'item.downloads', 0)}
      </div>
      <div className={styles.timespent}>
        {analtyics?.loading ? <AnalyticsPlaceholder /> : duration}
      </div>
    </div>
  );
}

const AssetRowPlaceholder: React.FC<object> = () => {
  return (
    <div className={cx(styles.row, styles.placeholder)}>
      <div className={styles.asset}>
        <div className={cx(styles.icon, styles.block)}></div>
        <div className={styles.details}>
          <div className={cx(styles.name, styles.block)}></div>
          <div className={styles.metadata}>
            <span className={cx(styles.size, styles.block)}></span>
            <span className={cx(styles.fileType, styles.block)}></span>
          </div>
          <div className={cx(styles.updatedDate, styles.block)}></div>
        </div>
      </div>
      <div className={cx(styles.createdBy, styles.block)}></div>
      <div className={styles.views}>
        <AnalyticsPlaceholder />
      </div>
      <div className={styles.shares}>
        <AnalyticsPlaceholder />
      </div>
      <div className={styles.downloads}>
        <AnalyticsPlaceholder />
      </div>
      <div className={styles.timespent}>
        <AnalyticsPlaceholder />
      </div>
    </div>
  );
}

const AnalyticsPlaceholder: React.FC<object> = () => {
  return (
    <div className={cx(styles.analytics, styles.placeholder)}>
      <div className={cx(styles.value, styles.block)}></div>
    </div>
  );
}

export { UncategorizedCollectionPage };